<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="row no-gutters h-100">
      <div class="col-3 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Daily Order Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyDailyChartFilter"
              @reset="resetDailyChartFilter">
              <template v-slot:popover>
                <ReportFilterGroup label="Date Range">
                  <ReportDateRangeSelect 
                    :startDate="editingDailyChartFilter.startDate"
                    :endDate="editingDailyChartFilter.endDate"
                    @updateStartDate="editingDailyChartFilter.startDate = $event"
                    @updateEndDate="editingDailyChartFilter.endDate = $event"
                  />
                </ReportFilterGroup>
                <ReportFilterGroup label="Stock Filter">
                  <FormMultiSelect v-model="editingDailyChartFilter.stockList" :options="stockLookupList" />
                </ReportFilterGroup>
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedDailyChartFilter.startDate | formatDate}} - {{appliedDailyChartFilter.endDate | formatDate}}
                </div>
                <div v-if="appliedDailyChartFilter.stockList && appliedDailyChartFilter.stockList.length > 0">
                  <i class="far fa-inventory mr-2"></i>{{appliedDailyChartFilter.stockList.length}} Stock{{displayCountPlural(appliedDailyChartFilter.stockList.length)}}
                </div>
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="false">
          <div class="col">
            <AnalyticsDashboardValue highlight>
              <template v-slot:info></template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-9">
        <ChartBar
          title="Daily Optimiser Sheets"
          :series="sheetDailyValueData.series"
          :xAxis="sheetDailyValueData.xAxis"
          :yAxis="sheetDailyValueData.yAxis"
          :legend="sheetDailyValueData.legend"
          :options="sheetDailyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import ChartBar from '@/components/charts/chartBar'

import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'OptimiserDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      optimiserSummaryData: {
        
      },
      stockList: [],
      stockLookupList: [],
      sheetDailyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultDailyChartFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        stockList: []
      },
      editingDailyChartFilter: {},
      appliedDailyChartFilter: {}
    }
  },
  computed: {
  },
  mounted() {
    this.editingDailyChartFilter = _.clone(this.defaultDailyChartFilter)
    this.appliedDailyChartFilter = _.clone(this.defaultDailyChartFilter)

    this.requestData()
    this.requestChartData()

    this.subscribeEndpoint('/production/sheetschedule', () => {
      this.reloadData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/sheetschedule')
  },
  methods: {
    requestData() {
      this.$apiService.inventoryService.getStockList({}, { isActive: 1, type: InventoryStockTypes.glass })
      .then(response => {
        this.stockList = response.data
        this.stockLookupList = this.stockList.map(stock => {
          return {
            value: stock.id,
            name: `${stock.code} | ${stock.name}`
          }
        })
      })
    },
    applyDailyChartFilter() {
      this.appliedDailyChartFilter = _.clone(this.editingDailyChartFilter)

      this.requestChartData()
    },
    resetDailyChartFilter() {
      this.editingDailyChartFilter = _.clone(this.defaultDailyChartFilter)
      this.appliedDailyChartFilter = _.clone(this.defaultDailyChartFilter)

      this.requestChartData()
    },
    requestChartData() {
      var chartFilter = {
        type: 'daily',
        startDate: this.appliedDailyChartFilter.startDate,
        endDate: this.appliedDailyChartFilter.endDate
      }
      if(this.appliedDailyChartFilter.stockList && this.appliedDailyChartFilter.stockList.length > 0)
        chartFilter.stockIdList = this.appliedDailyChartFilter.stockList

      var widgetComp = this
      this.$apiService.analyticsService.getOptimiserStatsChartData(chartFilter)
      .then(response => {
        let chartData = response.data

        let sheetAreaTooltip = {
          valueFormatter(value) {
            return widgetComp.formatDecimal(value, 2, true) + ' m2';
          }
        }

        this.sheetDailyValueData.legend = {
        }
        this.sheetDailyValueData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM'))
        }
        this.sheetDailyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return widgetComp.formatInteger(value, false, true) + ' m2';
              }
            }
          },
          { type: 'value', alignTicks: true }
        ]
        this.sheetDailyValueData.series = []
        this.sheetDailyValueData.series.push({
          type: 'bar',
          name: 'Used Area',
          stack: 'Sheet',
          data: chartData.map(chartEntry => chartEntry.totalStatUsedArea),
          tooltip: sheetAreaTooltip
        })
        this.sheetDailyValueData.series.push({
          type: 'bar',
          name: 'Offcut Area',
          stack: 'Sheet',
          data: chartData.map(chartEntry => chartEntry.totalStatOffcutArea),
          tooltip: sheetAreaTooltip
        })
        this.sheetDailyValueData.series.push({
          type: 'bar',
          name: 'Defect Area',
          stack: 'Sheet',
          data: chartData.map(chartEntry => chartEntry.totalStatDefectArea),
          tooltip: sheetAreaTooltip
        })
        this.sheetDailyValueData.series.push({
          type: 'bar',
          name: 'Waste Area',
          stack: 'Sheet',
          data: chartData.map(chartEntry => chartEntry.totalStatWasteArea),
          tooltip: sheetAreaTooltip
        })
        this.sheetDailyValueData.series.push({
          type: 'bar',
          name: 'Split Sheet Area',
          stack: 'Sheet',
          data: chartData.map(chartEntry => chartEntry.totalStatSplitSheetArea),
          tooltip: sheetAreaTooltip
        })

        this.sheetDailyValueData.series.push({
          type: 'line',
          name: 'Sheet Count',
          yAxisIndex: 1,
          data: chartData.map(chartEntry => chartEntry.totalSheetCount)
        })
      })
    },
  }
}
</script>